import React, { useEffect, useMemo, useRef, useState } from 'react';
import InlineAtHomeSupportPanel from '../../components/InlineAtHomeSupportPanel';
import { fetchStandardsProgressionData } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { isReadOnlyTeacherDemo } from '../../utils/demoAccess';
import { fetchFamilyDescriptions } from '../../utils/familyDescriptions';
import {
  CORE_GRADE_ORDER,
  SUBJECT_PRIORITY,
  UI_COLORS,
  canonicalSubject,
  normalizeLiteracyWritingStrand,
  normalizedGradeLabel,
  resolveStrandLabel,
} from '../../utils/progressStatus';

const GRADE_ALIASES = { KG: 'K', '0': 'K', 'PRE-K': 'PK', PREK: 'PK' };
const MAX_SEARCH_RESULTS = 60;

function normalizeGrade(token) {
  const value = String(token || '').trim().toUpperCase();
  return GRADE_ALIASES[value] || value.replace(/^0+(?=\d)/, '');
}

// Grade bands such as "3-5" apply to every grade they cover.
export function gradesForStandard(gradeLevel) {
  const parts = String(gradeLevel || '').split('-').map(normalizeGrade);
  const start = CORE_GRADE_ORDER.indexOf(parts[0]);
  const end = CORE_GRADE_ORDER.indexOf(parts[1] || parts[0]);
  if (start < 0 || end < start) return [];
  return CORE_GRADE_ORDER.slice(start, end + 1);
}

function subjectOrder(a, b) {
  const pa = SUBJECT_PRIORITY[a.toLowerCase()] || 99;
  const pb = SUBJECT_PRIORITY[b.toLowerCase()] || 99;
  return pa - pb || a.localeCompare(b);
}

function codeSort(a, b) {
  return a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' });
}

export function buildActionCardCatalog(rows, descriptions = {}) {
  const subjects = new Map();
  const seen = new Set();
  (rows || []).forEach((row) => {
    const code = String(row?.mls_code || '').trim();
    if (!code) return;
    const subject = canonicalSubject(row.subject);
    const family = descriptions[code] || null;
    const domain = String(row.domain_description || row.domain || '').trim() || 'General';
    const strand = normalizeLiteracyWritingStrand(row.subject, row.grade_level, code, resolveStrandLabel(row))
      || resolveStrandLabel(row)
      || 'General';
    gradesForStandard(row.grade_level).forEach((grade) => {
      const key = `${subject}|${grade}|${code}`;
      if (seen.has(key)) return;
      seen.add(key);
      if (!subjects.has(subject)) subjects.set(subject, new Map());
      const grades = subjects.get(subject);
      if (!grades.has(grade)) grades.set(grade, new Map());
      const domains = grades.get(grade);
      if (!domains.has(domain)) domains.set(domain, { name: domain, familyText: '', strands: new Map() });
      const domainEntry = domains.get(domain);
      if (!domainEntry.familyText && family?.domain) domainEntry.familyText = family.domain;
      if (!domainEntry.strands.has(strand)) domainEntry.strands.set(strand, { name: strand, familyText: '', standards: [] });
      const strandEntry = domainEntry.strands.get(strand);
      if (!strandEntry.familyText && family?.strand) strandEntry.familyText = family.strand;
      strandEntry.standards.push({
        code,
        subject,
        grade,
        domain,
        strand,
        description: family?.standard || String(row.standard_description || row.cluster_expectation_description || '').trim() || code,
        familySupport: family,
      });
    });
  });

  return [...subjects.keys()].sort(subjectOrder).map((subject) => {
    const grades = subjects.get(subject);
    return {
      subject,
      grades: CORE_GRADE_ORDER.map((grade) => {
        const domains = grades.get(grade);
        const domainList = domains ? [...domains.values()].map((domain) => ({
          ...domain,
          strands: [...domain.strands.values()].map((strand) => ({ ...strand, standards: strand.standards.sort(codeSort) })),
        })) : [];
        const count = domainList.reduce((total, domain) => total + domain.strands.reduce((sum, strand) => sum + strand.standards.length, 0), 0);
        return { grade, domains: domainList, count };
      }),
    };
  });
}

const cardStyle = { background: '#fff', border: '1px solid var(--ae-border)', borderRadius: 14, boxShadow: '0 8px 20px rgba(15, 23, 42, 0.05)' };
const familyTextStyle = { display: 'block', marginTop: 5, fontSize: 12, fontWeight: 500, lineHeight: 1.55, color: 'var(--ae-text-muted)' };
const caret = (open, size = 11) => (
  <span aria-hidden="true" style={{ fontSize: size, color: 'var(--ae-text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
);

function StandardTile({ standard, open, onToggle, canManage }: any) {
  return (
    <div className="clear-progress-standard-item" style={{ minWidth: 0, gridColumn: '1 / -1' }}>
      <div className="clear-progress-standard-card" style={{ border: open ? '2px solid #1e3a5f' : '1px solid rgba(17, 24, 39, 0.08)', borderRadius: 10, background: '#fff', boxShadow: '0 2px 6px rgba(15, 23, 42, 0.08)', minWidth: 0, overflow: 'hidden' }}>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={`Action Card for ${standard.code}`}
          title={open ? 'Hide this Action Card' : 'Open this Action Card to add school activity resources'}
          style={{ width: '100%', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', gap: 10, padding: '8px 10px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', overflowWrap: 'anywhere' }}
        >
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 9, fontWeight: 800, color: '#64748b', marginBottom: 3 }}>{standard.code}</span>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 500, lineHeight: 1.55, color: '#1f2937' }}>{standard.description}</span>
          </span>
          {caret(open, 12)}
        </button>
      </div>
      {open && (
        <InlineAtHomeSupportPanel
          standard={standard.code}
          grade={standard.grade}
          subject={standard.subject}
          strand={standard.strand}
          domain={standard.domain}
          useFamilySupport
          familySupport={standard.familySupport}
          canManageSchoolResources={canManage}
          showStudentStatus={false}
          schoolWideResources
        />
      )}
    </div>
  );
}

export default function FamilyActionCards() {
  const { user } = useAuth();
  const canManage = !isReadOnlyTeacherDemo(user);
  const [rows, setRows] = useState<any>([]);
  const [descriptions, setDescriptions] = useState<any>({});
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [expandedSubjects, setExpandedSubjects] = useState<any>({});
  const [selectedGrade, setSelectedGrade] = useState<any>({});
  const [expandedDomain, setExpandedDomain] = useState<any>(null);
  const [expandedStrand, setExpandedStrand] = useState<any>(null);
  const [openCard, setOpenCard] = useState<any>(null);
  const [search, setSearch] = useState<any>('');
  const gradeTileRefs = useRef<any>({});
  const gradeCarouselRefs = useRef<any>({});

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([fetchStandardsProgressionData(), fetchFamilyDescriptions()])
      .then(([standardsResult, descriptionsResult]) => {
        if (cancelled) return;
        if (standardsResult.status === 'fulfilled') {
          setRows(Array.isArray(standardsResult.value) ? standardsResult.value : []);
        } else {
          setError('Could not load standards for your school.');
        }
        if (descriptionsResult.status === 'fulfilled') setDescriptions(descriptionsResult.value || {});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const catalog = useMemo(() => buildActionCardCatalog(rows, descriptions), [rows, descriptions]);

  const searchTerm = search.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!searchTerm) return [];
    const results = [];
    catalog.forEach((subject) => subject.grades.forEach((grade) => grade.domains.forEach((domain) => domain.strands.forEach((strand) => {
      strand.standards.forEach((standard) => {
        const haystack = [standard.code, standard.description, domain.name, domain.familyText, strand.name, strand.familyText, subject.subject].join(' ').toLowerCase();
        if (haystack.includes(searchTerm)) results.push(standard);
      });
    }))));
    return results;
  }, [catalog, searchTerm]);

  // Keep the selected (or first populated) grade centred in each carousel, like the family view.
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      catalog.forEach(({ subject, grades }) => {
        const target = selectedGrade[subject] || grades.find((grade) => grade.count)?.grade;
        const tile = gradeTileRefs.current[`${subject}|${target}`];
        const carousel = gradeCarouselRefs.current[subject];
        if (!tile || !carousel || typeof carousel.scrollTo !== 'function') return;
        const tileRect = tile.getBoundingClientRect();
        const carouselRect = carousel.getBoundingClientRect();
        const centre = carousel.scrollLeft + tileRect.left - carouselRect.left + (tileRect.width / 2);
        carousel.scrollTo({ left: Math.max(0, centre - (carousel.clientWidth / 2)), behavior: 'smooth' });
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [catalog, selectedGrade, searchTerm]);

  const toggleCard = (key) => setOpenCard((current) => (current === key ? null : key));
  const resetDrillDown = () => {
    setExpandedDomain(null);
    setExpandedStrand(null);
    setOpenCard(null);
  };

  const selectGrade = (subject, grade, isExpanded) => {
    const isSelected = selectedGrade[subject] === grade;
    if (isSelected && isExpanded) {
      setExpandedSubjects((current) => ({ ...current, [subject]: false }));
    } else {
      setSelectedGrade((current) => ({ ...current, [subject]: grade }));
      setExpandedSubjects((current) => ({ ...current, [subject]: true }));
    }
    resetDrillDown();
  };

  const renderStandards = (standards, keyPrefix) => (
    <div className="clear-progress-standard-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 6 }}>
      {standards.map((standard) => {
        const key = `${keyPrefix}|${standard.subject}|${standard.grade}|${standard.code}`;
        return <StandardTile key={key} standard={standard} open={openCard === key} onToggle={() => toggleCard(key)} canManage={canManage} />;
      })}
    </div>
  );

  const renderDomains = (subject, grade) => (
    <div className="clear-progress-domain-list" style={{ display: 'grid', gap: 10, marginBottom: 12, padding: 10, background: '#c9dbf2', border: '1px solid #b4cbea', borderRadius: 14, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55)' }}>
      {grade.domains.length === 0 ? (
        <div style={{ padding: 10, background: 'var(--ae-surface)', borderRadius: 12, color: 'var(--ae-text-muted)', fontSize: 13 }}>
          No standards are set up for {normalizedGradeLabel(grade.grade)} yet.
        </div>
      ) : grade.domains.map((domain) => {
        const domainKey = `${subject}|${grade.grade}|${domain.name}`;
        const domainOpen = expandedDomain === domainKey;
        return (
          <div key={domainKey} className="clear-progress-domain-card" style={{ minWidth: 0, background: 'var(--ae-surface)', border: '1px solid #d7e1ef', borderRadius: 12, padding: 10, overflow: 'hidden', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.05)' }}>
            <div className="clear-progress-header-tile" style={{ background: '#fff', border: domainOpen ? '2px solid #1e3a5f' : '1px solid rgba(15, 23, 42, 0.08)', borderRadius: 9 }}>
              <button
                type="button"
                aria-expanded={domainOpen}
                onClick={() => {
                  setExpandedDomain(domainOpen ? null : domainKey);
                  setExpandedStrand(null);
                  setOpenCard(null);
                }}
                title={`${domain.name} · ${normalizedGradeLabel(grade.grade)} · click to see strands`}
                style={{ width: '100%', minWidth: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', gap: 10, background: 'transparent', border: 'none', borderRadius: 9, padding: '9px 10px', cursor: 'pointer', textAlign: 'left' }}
              >
                <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 800, color: 'var(--ae-primary-strong)' }}>{domain.name}</span>
                  {domain.familyText && <span style={familyTextStyle}>{domain.familyText}</span>}
                </span>
                {caret(domainOpen)}
              </button>
            </div>
            {domainOpen && (
              <div style={{ minWidth: 0, marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5edf7' }}>
                <div className="clear-progress-strand-list" style={{ display: 'grid', gap: 8 }}>
                  {domain.strands.map((strand) => {
                    const strandKey = `${domainKey}|${strand.name}`;
                    const strandOpen = expandedStrand === strandKey;
                    return (
                      <div key={strandKey} className="clear-progress-strand-card" style={{ minWidth: 0, background: '#f8fbff', border: '1px solid var(--ae-border)', borderRadius: 10, padding: 8, overflow: 'hidden' }}>
                        <div className="clear-progress-header-tile" style={{ background: '#fff', borderRadius: 8 }}>
                          <button
                            type="button"
                            aria-expanded={strandOpen}
                            onClick={() => {
                              setExpandedStrand(strandOpen ? null : strandKey);
                              setOpenCard(null);
                            }}
                            title={`${strand.name} · click to see standards`}
                            style={{ width: '100%', minWidth: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', gap: 10, background: 'transparent', border: 'none', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', textAlign: 'left' }}
                          >
                            <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                              <span style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#1e3a5f' }}>{strand.name}</span>
                              {strand.familyText && <span style={familyTextStyle}>{strand.familyText}</span>}
                            </span>
                            {caret(strandOpen, 12)}
                          </button>
                        </div>
                        {strandOpen && (
                          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--ae-border)' }}>
                            {renderStandards(strand.standards, strandKey)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="clear-progress-family" style={{ maxWidth: 980, margin: '0 auto', padding: '8px 4px 22px' }}>
      <section aria-label="Family Action Cards" style={{ ...cardStyle, marginBottom: 16, padding: '18px 20px' }}>
        <h1 style={{ margin: '0 0 6px', fontSize: 26, fontWeight: 800, color: 'var(--ae-primary-strong)' }}>Family Action Cards</h1>
        <p style={{ margin: '0 0 14px', color: 'var(--ae-text-muted)', fontSize: 14, lineHeight: 1.5 }}>
          Browse every standard the way families see it on Student Profiles: pick a subject and grade, open a domain and strand,
          then open a standard's Action Card to add a resource under Activity. It is shared with every family and student in your school.
        </p>
        <label htmlFor="family-action-card-search" style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 700 }}>Search standards</label>
        <input
          id="family-action-card-search"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search code, learning outcome, domain, or strand…"
          style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid var(--ae-border)', borderRadius: 8, fontSize: 14 }}
        />
      </section>

      {loading ? (
        <div role="status" style={{ ...cardStyle, padding: 18, color: 'var(--ae-text-muted)' }}>Loading Action Cards…</div>
      ) : error ? (
        <div role="alert" style={{ ...cardStyle, padding: 18, color: '#991b1b' }}>{error}</div>
      ) : searchTerm ? (
        <section aria-label="Search results" style={{ ...cardStyle, padding: 14 }}>
          <div style={{ marginBottom: 10, color: '#475569', fontSize: 13, fontWeight: 700 }}>
            {searchResults.length === 0 ? 'No standards match your search.' : `${searchResults.length} matching standard${searchResults.length === 1 ? '' : 's'}`}
            {searchResults.length > MAX_SEARCH_RESULTS && ` — showing the first ${MAX_SEARCH_RESULTS}`}
          </div>
          {renderStandards(searchResults.slice(0, MAX_SEARCH_RESULTS), 'search')}
        </section>
      ) : catalog.length === 0 ? (
        <div style={{ ...cardStyle, padding: 18, color: 'var(--ae-text-muted)' }}>No standards are set up for your school yet.</div>
      ) : (
        catalog.map(({ subject, grades }) => {
          const isExpanded = Boolean(expandedSubjects[subject]);
          const activeGrade = grades.find((grade) => grade.grade === selectedGrade[subject]) || null;
          return (
            <section key={subject} aria-label={`${subject} Action Cards`} style={{ marginBottom: 16, background: '#dce8f7', borderRadius: 14, padding: '10px 12px 14px', border: '1px solid #c7d7ef' }}>
              <button
                type="button"
                onClick={() => {
                  setExpandedSubjects((current) => ({ ...current, [subject]: !isExpanded }));
                  resetDrillDown();
                }}
                aria-expanded={isExpanded}
                title={isExpanded ? 'Collapse this subject' : 'Pick a grade to see its domains'}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: UI_COLORS.frame, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 800, fontSize: 24, letterSpacing: 0.2, marginBottom: 12, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)', cursor: 'pointer' }}
              >
                {subject}
                <span aria-hidden="true" style={{ fontSize: 16, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
              </button>

              <div
                role="group"
                aria-label={`${subject} grades`}
                ref={(element) => { if (element) gradeCarouselRefs.current[subject] = element; }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, overflowX: 'auto', padding: '10px max(12px, calc(50% - 76px)) 14px', margin: '0 -2px 12px', scrollbarWidth: 'thin', scrollSnapType: 'x mandatory' }}
              >
                {grades.map((grade) => {
                  const isSelected = activeGrade?.grade === grade.grade;
                  const isActive = isSelected && isExpanded;
                  return (
                    <button
                      key={grade.grade}
                      type="button"
                      className="clear-progress-grade-card"
                      aria-pressed={isActive}
                      ref={(element) => { if (element) gradeTileRefs.current[`${subject}|${grade.grade}`] = element; }}
                      onClick={() => selectGrade(subject, grade.grade, isExpanded)}
                      title={isActive ? 'Click to collapse this grade' : 'Click to show this grade’s domains'}
                      style={{ flex: `0 0 ${isActive ? 150 : 104}px`, minHeight: isActive ? 84 : 68, background: grade.count ? '#fff' : UI_COLORS.nA, border: isActive ? '2px solid #1e3a5f' : '1px solid rgba(15, 23, 42, 0.08)', borderRadius: 14, padding: isActive ? 12 : 9, boxShadow: isActive ? '0 10px 22px rgba(15, 23, 42, 0.16)' : '0 6px 14px rgba(15, 23, 42, 0.05)', cursor: 'pointer', textAlign: 'center', transform: isActive ? 'scale(1.08)' : 'scale(0.94)', opacity: isActive ? 1 : 0.82, scrollSnapAlign: 'center', transition: 'flex-basis 0.2s ease, transform 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease' }}
                    >
                      <span style={{ display: 'block', fontSize: isActive ? 12 : 10, fontWeight: 800, color: 'var(--ae-primary-strong)' }}>
                        {normalizedGradeLabel(grade.grade)}
                      </span>
                    </button>
                  );
                })}
              </div>

              {isExpanded && activeGrade && renderDomains(subject, activeGrade)}
            </section>
          );
        })
      )}
    </div>
  );
}
